import {
  InputOption,
  InterviewSessionStatus,
  InterviewQuestionInputType,
  Message,
  OrchestratorState,
  SessionMessage,
  SessionMetadata,
} from "./interview.types";

export enum BenchmarkProvider {
  OPENAI = "openai",
  ANTHROPIC = "anthropic",
  OPENROUTER = "openrouter",
}

export enum BenchmarkLaneStatus {
  CONNECTING = "connecting",
  WAITING = "waiting",
  READY = "ready",
  COMPLETE = "complete",
  ERROR = "error",
}

export enum BenchmarkEvaluationVerdict {
  PASS = "pass",
  FAIL = "fail",
}

export enum BenchmarkEvaluationSaveStatus {
  SAVING = "saving",
  SAVED = "saved",
  ERROR = "error",
}

export interface BenchmarkEvaluationState {
  verdict: BenchmarkEvaluationVerdict;
  status: BenchmarkEvaluationSaveStatus;
  resultId?: string;
  evaluatedAt?: string;
  error?: string;
}

export enum BenchmarkResponseVerdict {
  GOOD = "good",
  BAD = "bad",
}

export type BenchmarkResponseDimension =
  | "accuracy"
  | "relevance"
  | "tone"
  | "interview_flow"
  | "formatting"
  | "safety"
  | "other";

export interface BenchmarkAnnotationMessage {
  id: string;
  role: string;
  content: string;
  questionId?: string;
  inputType?: string;
}

export interface SaveBenchmarkResponseAnnotationRequest {
  benchmarkId: string;
  laneId: string;
  sessionId: string;
  turnId: string;
  messageId: string;
  replacesAnnotationId?: string;
  verdict: BenchmarkResponseVerdict;
  dimension: BenchmarkResponseDimension;
  reason: string;
  provider: BenchmarkProvider;
  model: string;
  routedProvider?: string;
  response: string;
  question: {
    id?: string;
    content?: string;
    inputType?: string;
    options?: string[];
  };
  candidateResponse?: string;
  conversationPrefix: BenchmarkAnnotationMessage[];
  stateSnapshot: Record<string, unknown>;
  metrics: {
    latencyMs?: number;
    inputTokens: number;
    outputTokens: number;
    reasoningTokens: number;
    totalTokens: number;
    costUsd: number;
    costCoverage: BenchmarkEvaluationUsage["costCoverage"];
  };
}

export interface BenchmarkResponseAnnotation extends SaveBenchmarkResponseAnnotationRequest {
  version: 1;
  id: string;
  annotatedAt: string;
}

export interface GetBenchmarkResponseAnnotationsResponse {
  annotations: BenchmarkResponseAnnotation[];
}

export interface BenchmarkModelSelection {
  provider: BenchmarkProvider;
  model: string;
}

export interface BenchmarkLaneState extends BenchmarkModelSelection {
  id: string;
  label: string;
  sessionId: string;
  status: BenchmarkLaneStatus;
  messages: Message[];
  traceEvents?: BenchmarkTraceEvent[];
  lastLatencyMs?: number;
  error?: string;
  canRetrySubmission?: boolean;
  isAutoRetrying?: boolean;
  evaluation?: BenchmarkEvaluationState;
  responseAnnotations?: Record<string, BenchmarkResponseAnnotation>;
}

export type BenchmarkTraceEventType =
  | "turn.started"
  | "turn.completed"
  | "turn.error"
  | "node.started"
  | "node.completed"
  | "node.error"
  | "route.selected"
  | "llm.started"
  | "llm.completed"
  | "llm.error";

export interface BenchmarkTraceEvent {
  type: BenchmarkTraceEventType;
  benchmarkId: string;
  laneId: string;
  sessionId: string;
  turnId: string;
  sequence: number;
  timestamp: string;
  elapsedMs: number;
  name?: string;
  spanId?: string;
  durationMs?: number;
  decision?: string;
  provider?: string;
  model?: string;
  openRouterRoutingMode?: "latency" | "throughput";
  openRouterProvider?: string;
  openRouterModel?: string;
  openRouterAttempt?: number;
  openRouterStrategy?: string;
  operationName?: string;
  promptName?: string;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  reasoningTokens?: number;
  costUsd?: number;
  costSource?: "provider" | "estimated";
  estimatedCostUsd?: number;
  error?: string;
  stateChanges?: Record<string, string | number | boolean | null>;
}

export interface BenchmarkAnswer {
  content: string;
  selectedOptions?: InputOption[];
}

export interface BenchmarkLaneQuestion {
  laneId: string;
  laneLabel: string;
  prompt: string;
  inputType: InterviewQuestionInputType;
  options: InputOption[];
}

export interface BenchmarkActiveQuestion {
  key: string;
  inputType: InterviewQuestionInputType;
  options: InputOption[];
  canAnswer: boolean;
  laneQuestions?: BenchmarkLaneQuestion[];
  requiresLaneAnswers?: boolean;
  issue?: string;
}

export interface GenerateBenchmarkCandidateResponseRequest {
  questions: string[];
  priorCandidateResponses?: string[];
}

export interface GenerateBenchmarkCandidateResponseResponse {
  content: string;
  model: string;
}

export interface CancelBenchmarkSessionsRequest {
  benchmarkId: string;
  sessionIds: string[];
}

export interface CancelBenchmarkSessionsResponse {
  cancelledSessionIds: string[];
}

export interface BenchmarkOpenRouterModelUsage {
  model: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  costUsd: number;
  providerCostCount: number;
  estimatedCostCount: number;
  unpricedRequestCount: number;
}

export interface BenchmarkOpenRouterUsage {
  totalUsageUsd: number;
  dailyUsageUsd: number;
  weeklyUsageUsd: number;
  monthlyUsageUsd: number;
  limitUsd: number | null;
  limitRemainingUsd: number | null;
  benchmarkTrackedUsageUsd: number;
  benchmarkTrackedSince: string | null;
  benchmarkModels: BenchmarkOpenRouterModelUsage[];
  attributionTitle: string;
  attributionReferer: string;
  attributionUser: string;
  updatedAt: string;
}

export interface BenchmarkEvaluationUsage {
  inferenceCount: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  costUsd: number;
  effectiveCostPerThousandTokensUsd?: number;
  costCoverage: "none" | "provider" | "estimated" | "mixed";
  unpricedInferenceCount: number;
}

export interface SaveBenchmarkEvaluationRequest {
  benchmarkId: string;
  laneId: string;
  sessionId: string;
  verdict: BenchmarkEvaluationVerdict;
  provider: BenchmarkProvider;
  model: string;
  turnCount: number;
  messageCount: number;
  transcript: SessionMessage[];
  stateSnapshot: {
    sessionStatus: InterviewSessionStatus;
    orchestratorState: OrchestratorState;
    sessionMetadata: SessionMetadata;
    startedAt: Date | null;
    completedAt: Date | null;
    laneStatus: BenchmarkLaneStatus;
    lastLatencyMs?: number;
    laneError?: string;
    traceEvents: BenchmarkTraceEvent[];
  };
  usage: BenchmarkEvaluationUsage;
}

export interface SaveBenchmarkEvaluationResponse {
  id: string;
  evaluatedAt: string;
}
